/**
 * 平台管理路由配置
 * 为平台管理员提供专属路由，使用 PlatformLayout 作为布局
 * 用于管理租户、订阅计划、计费等平台级功能
 */

import React, { lazy, Suspense } from 'react';
import { RouteObject, Outlet } from 'react-router-dom';
import { Spin } from 'antd';
import PlatformLayout from '@/layouts/PlatformLayout';

// 平台仪表盘
const PlatformDashboard = lazy(() => import('@/pages/Platform/Dashboard'));

// 租户管理页面
const TenantListPage = lazy(() => import('@/pages/Platform/Tenants/TenantList'));
// REQ-20260617-0018: 独立查看页 + 编辑页（替代原抽屉 + 旧 TenantDetail 占位页）
const TenantViewPage = lazy(() => import('@/pages/Platform/Tenants/TenantViewPage'));
const TenantEditPage = lazy(() => import('@/pages/Platform/Tenants/TenantEditPage'));
const PendingTenantsPage = lazy(() => import('@/pages/Platform/Tenants/PendingTenants'));

// 租户池管理页面
const TenantPoolListPage = lazy(() => import('@/pages/Platform/TenantPools/TenantPoolList'));

// 订阅管理页面
const PlanListPage = lazy(() => import('@/pages/Platform/Subscription/PlanList'));
const SubscriptionListPage = lazy(() => import('@/pages/Platform/Subscription/SubscriptionList'));

// 计费管理页面
const OrderListPage = lazy(() => import('@/pages/Platform/Billing/OrderList'));
const InvoiceListPage = lazy(() => import('@/pages/Platform/Billing/InvoiceList'));
const PaymentListPage = lazy(() => import('@/pages/Platform/Billing/PaymentList'));

// 数据统计页面
const StatisticsPage = lazy(() => import('@/pages/Platform/Statistics'));

// AI 管理页面
const FeatureGapListPage = lazy(() => import('@/pages/Platform/AI/FeatureGapList'));

// 系统设置页面
const PlatformSettingsPage = lazy(() => import('@/pages/Platform/Settings/PlatformSettings'));
const AdminListPage = lazy(() => import('@/pages/Platform/Settings/AdminList'));
const OperationLogPage = lazy(() => import('@/pages/Platform/Settings/OperationLog'));

// 统一的加载中组件
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: 200
  }}>
    <Spin size="large" tip="加载中..."><div style={{ minHeight: 100 }} /></Spin>
  </div>
);

// 包裹懒加载组件的Suspense高阶函数
const withSuspense = (Component: React.LazyExoticComponent<React.FC<any>>) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

/**
 * 平台管理路由配置
 * 这些路由需要平台管理员权限，使用 PlatformLayout 布局
 */
export const platformRoutes: RouteObject = {
  path: '/platform',
  element: <PlatformLayout />,
  children: [
    // 平台仪表盘
    {
      index: true,
      element: withSuspense(PlatformDashboard),
    },
    {
      path: 'dashboard',
      element: withSuspense(PlatformDashboard),
    },

    // ==================== 租户管理 ====================
    {
      path: 'tenants',
      element: <Outlet />,
      children: [
        { index: true, element: withSuspense(TenantListPage) },
        { path: 'pending', element: withSuspense(PendingTenantsPage) },
        { path: ':id/edit', element: withSuspense(TenantEditPage) },
        { path: ':id', element: withSuspense(TenantViewPage) },
      ],
    },

    // ==================== 租户池管理 ====================
    {
      path: 'tenant-pools',
      element: withSuspense(TenantPoolListPage),
    },

    // ==================== 订阅管理 ====================
    {
      path: 'plans',
      element: withSuspense(PlanListPage),
    },
    {
      path: 'subscriptions',
      element: withSuspense(SubscriptionListPage),
    },

    // ==================== 计费管理 ====================
    {
      path: 'orders',
      element: withSuspense(OrderListPage),
    },
    {
      path: 'invoices',
      element: withSuspense(InvoiceListPage),
    },
    {
      path: 'payments',
      element: withSuspense(PaymentListPage),
    },

    // ==================== AI 管理 ====================
    {
      path: 'ai/feature-gaps',
      element: withSuspense(FeatureGapListPage),
    },

    // ==================== 数据统计 ====================
    {
      path: 'statistics',
      element: withSuspense(StatisticsPage),
    },

    // ==================== 系统设置 ====================
    {
      path: 'settings',
      element: withSuspense(PlatformSettingsPage),
    },
    {
      path: 'admins',
      element: withSuspense(AdminListPage),
    },
    {
      path: 'logs',
      element: withSuspense(OperationLogPage),
    },
  ],
};

export default platformRoutes;
